import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  invalidateQueries,
  QueryOptions,
  useMutation,
  useQuery,
} from '@globalconnect/query';
import { PaginationResponse } from '@ip-only/front-end-library';
import { map, Observable } from 'rxjs';
import { FeedbackAlertTypes } from '../../../shared/feedback-alert/feedback-alert-entities';
import { FeedbackAlertService } from '../../../shared/feedback-alert/service/feedback-alert.service';
import {
  Buildings,
  DeliveryPoints,
  GroupAgreements,
  GroupAgreementsDetails,
  PaginationData,
} from '../../entities/installation-reporting-mdu.entities';
import { InstallationReportingMduApiService } from '../../group-agreement-details-page/installation-reporting-mdu-api.service';
import {
  BuildingInstallationReport,
  DeliveryPointInstallationReport,
  GroupAgreementDetails,
  GroupAgreementInstallationReport,
} from '../../entities/installation-reporting-mdu-details.entities';

export const mduExcelFileKey = 'mdu-excel-file';

type ExcelFile = { fileName: string; file: Blob };

const toPagination = <T>(
  response: PaginationResponse<T>,
): PaginationData & { items: T[] } => ({
  items: response.items ?? [],
  totalItems: response.totalItems ?? response.items?.length ?? 0,
  totalPages: response.totalPages ?? 1,
  currentPage: response.currentPage ?? 1,
  currentCount: response.currentCount ?? response.items?.length ?? 0,
});

@Injectable({
  providedIn: 'root',
})
export class InstallationReportingMduService {
  private readonly _storedGroupAgreements = signal<GroupAgreements | null>(
    null,
  );
  readonly storedGroupAgreements = this._storedGroupAgreements.asReadonly();

  readonly totalGroupAgreements = computed<number | undefined>(
    () => this._storedGroupAgreements()?.totalItems,
  );

  private readonly _storedGroupAgreementsDetails =
    signal<GroupAgreementsDetails | null>(null);
  readonly storedGroupAgreementsDetails =
    this._storedGroupAgreementsDetails.asReadonly();

  private readonly _storedBuildings = signal<Buildings | null>(null);
  readonly storedBuildings = this._storedBuildings.asReadonly();

  private readonly _storedDeliveryPoints = signal<DeliveryPoints | null>(null);
  readonly storedDeliveryPoints = this._storedDeliveryPoints.asReadonly();

  private readonly _excelLoadingState = signal(false);
  readonly excelLoadingState = this._excelLoadingState.asReadonly();

  private readonly feedbackAlertService = inject(FeedbackAlertService);

  constructor(private apiService: InstallationReportingMduApiService) {}

  createExcelFile(
    ifs: string,
    options?: Partial<
      QueryOptions<ExcelFile, HttpErrorResponse, never, ExcelFile>
    >,
  ) {
    return useQuery(
      [mduExcelFileKey, ifs],
      () => this.apiService.fetchExcel(ifs),
      {
        cacheTime: 0,
        staleTime: 0,
        ...options,
      },
    ).getState$();
  }

  updateFromExcel = (ifs: string, formData: FormData) =>
    useMutation(() => this.apiService.updateFromExcel(ifs, formData), {
      onSuccess: () => {
        this.handleExcelMutationCleanup(
          'success',
          'ordersOverview.importSuccess',
        );
        invalidateQueries(['group-agreements', ifs]);
      },
      onError: (err) => {
        console.error(err);
        this.handleExcelMutationCleanup(
          'critical',
          'ordersOverview.importError',
        );
      },
      onMutate: () => {
        this.setExcelLoadingState(true);
      },
    }).mutate();

  setExcelLoadingState = (state: boolean): void =>
    this._excelLoadingState.set(state);

  private readonly handleExcelMutationCleanup = (
    feedbackAlert: FeedbackAlertTypes,
    message: string,
  ): void => {
    this.feedbackAlertService.showAlert(feedbackAlert, message);
    this.setExcelLoadingState(false);
  };

  getGroupAgreements$ = (
    ifs: string,
    options?: Partial<
      QueryOptions<GroupAgreements, HttpErrorResponse, never, GroupAgreements>
    >,
  ) =>
    useQuery(['group-agreements', ifs], () => this.fetchGroupAgreements(ifs), {
      onSuccess: (groupAgreements: GroupAgreements) => {
        this._storedGroupAgreements.set(groupAgreements);
      },
      onError: (err) => console.error(err),
      cacheTime: 0,
      staleTime: 0,
      ...options,
    }).getState$();

  getGroupAgreementsDetails$ = (id: string) =>
    useQuery(
      ['group-agreements-details', id],
      () => this.fetchGroupAgreementsDetails(id),
      {
        onSuccess: (groupAgreementsDetails: GroupAgreementsDetails) => {
          this._storedGroupAgreementsDetails.set(groupAgreementsDetails);
        },
        onError: (err) => console.error(err),
        cacheTime: 0,
        staleTime: 0,
      },
    ).getState$();

  getBuildings$ = (groupAgreementId: string) =>
    useQuery(
      ['buildings', groupAgreementId],
      () => this.fetchBuildings(groupAgreementId),
      {
        onSuccess: (buildings: Buildings) => {
          this._storedBuildings.set(buildings);
        },
        onError: (err) => console.error(err),
        cacheTime: 0,
        staleTime: 0,
      },
    ).getState$();

  getDeliveryPoints$ = (ifs: string, groupAgreementId: string) =>
    useQuery(
      ['delivery-points', groupAgreementId],
      () => this.fetchDeliveryPoints(ifs, groupAgreementId),
      {
        onSuccess: (deliveryPoints: DeliveryPoints) => {
          this._storedDeliveryPoints.set(deliveryPoints);
        },
        onError: (err) => console.error(err),
        cacheTime: 0,
        staleTime: 0,
      },
    ).getState$();

  private fetchGroupAgreements(ifs: string): Observable<GroupAgreements> {
    return this.apiService
      .getGroupAgreements(ifs)
      .pipe(
        map((response: PaginationResponse<GroupAgreementInstallationReport>) =>
          toPagination(response),
        ),
      );
  }

  private fetchGroupAgreementsDetails(
    id: string,
  ): Observable<GroupAgreementsDetails> {
    return this.apiService
      .getGroupAgreementDetails(id)
      .pipe(map((details: GroupAgreementDetails) => details));
  }

  private fetchBuildings(groupAgreementId: string): Observable<Buildings> {
    return this.apiService
      .getBuildings(groupAgreementId)
      .pipe(
        map((response: PaginationResponse<BuildingInstallationReport>) =>
          toPagination(response),
        ),
      );
  }

  private fetchDeliveryPoints(
    ifs: string,
    groupAgreementId: string,
  ): Observable<DeliveryPoints> {
    return this.apiService
      .getDeliveryPoints(ifs, groupAgreementId)
      .pipe(
        map((response: PaginationResponse<DeliveryPointInstallationReport>) =>
          toPagination(response),
        ),
      );
  }
}
